import {expectEither, expectOneOf, POST, useHttpClient} from '@raison/network-facade';
import {useMutation, useSuspenseQuery, UseSuspenseQueryOptions, type UseMutationOptions} from '@tanstack/react-query';
import {formatDate} from 'date-fns';
import {useAtomValue} from 'jotai/index';
import {Logout$} from '~/entities/user';
import {SessionExpiredError} from '~/entities/user/model/SessionExpiredError';
import {AccountForm} from '../ui/AccountStatementReportForm';
import {AccountSchema} from './schemas/accountSchema';
import {SuccessResponseSchema} from './schemas/successResponseSchema';

export function useAccountQuery(options?: Omit<UseSuspenseQueryOptions<AccountSchema>, 'queryKey' | 'queryFn'>) {
    const http = useHttpClient();

    return useSuspenseQuery({
        ...options,
        queryKey: ['/v1/reports/account'],
        queryFn: async () => {
            const response = await http('/v1/reports/account').then(
                ...expectEither({
                    200: AccountSchema,
                })
            );
            if (response.status === 'Abort') {
                throw response.reason;
            }

            return response.json;
        },
    });
}

export function useAccountMutation(
    options?: Omit<UseMutationOptions<SuccessResponseSchema, unknown, AccountForm>, 'mutationKey' | 'mutationFn'>
) {
    const http = useHttpClient();
    const logout = useAtomValue(Logout$);

    return useMutation<SuccessResponseSchema, unknown, AccountForm>({
        ...options,
        mutationKey: ['v1/reports/account'],
        mutationFn: async ({date, service}) => {
            const url = `/v1/reports/account/${service}`;
            const params = new URLSearchParams();
            if (date) {
                params.set('date', formatDate(date, 'yyyy-MM-dd'));
            }
            const response = await http(`${url}?${params}`, POST()).then(
                expectOneOf({
                    200: SuccessResponseSchema,
                    401: null,
                })
            );
            if (response.status === 401) {
                logout();
                throw new SessionExpiredError();
            }

            return response.json;
        },
    });
}
